import React, { createContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import trim from 'lodash/trim';
import isObject from 'lodash/isObject';
import { locationApi, storage } from '@vmos2/core';
import { selectJsonSetting } from '@vmos2/utils';
import { actionsNames, analyticsService } from '@vmos2/analytics';
import { useGoogleLocationApi } from './useGoogleLocationApi';
import { useTranslation } from 'react-i18next';

type LocationProviderProps = {
  settings: {},
  children: React$Element<any>,
};

type RefObject = { current: any };

type Coordinates = {
  latitude: number,
  longitude: number,
};

type GeocodeAddress = Coordinates & {
  id: string,
  label: string,
};

export const LocationContext: React$Context<?{}> = createContext({});

export const LocationProvider = ({ settings, children }: LocationProviderProps): React$Node => {
  const isMount = useRef(true);
  const [location, setLocation] = useState(null);
  const [postCode, setPostCode] = useState(null);
  const [deniedAccess, setDeniedAccess] = useState(null);
  const [saveToLocalStorage, setSaveToLocalStorage] = useState(true);

  const geolocationKey = getGeolocationKey(settings);
  const [googleMapsSetting, geoCodeSetting] = useMemo(() => [
    selectJsonSetting('googleMaps', settings),
    selectJsonSetting('geocode', settings),
  ]);
  const isGoogleMapsEnabled = googleMapsSetting.enabled;
  const isGeocodeEnabled = geoCodeSetting.enabled || isGoogleMapsEnabled;
  const { fetchPlaceDetails } = useGoogleLocationApi(googleMapsSetting);
  const { t } = useTranslation('geoLocation');
  useEffect(() => {
    if (!isMount.current && saveToLocalStorage) {
      saveLocalStorageLocation(postCode, location);
    }
    isMount.current = false;
  }, [postCode, location, saveToLocalStorage]);

  const resetLocation = useCallback(() => {
    setPostCode(null);
    setLocation(null);
  }, []);

  const getNavigatorGeolocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      setDeniedAccess(null);
      navigator.geolocation.getCurrentPosition(
        position => {
          setPostCode(null);
          setLocation({
            latitude: get(position, 'coords.latitude'),
            longitude: get(position, 'coords.longitude'),
            isNavigatorGeolocation: true,
          });
          analyticsService.sendEvent(actionsNames.locationLoadedSuccess, { properties: { method: 'useMyLocation' } });
          resolve();
        },
        e => {
          setDeniedAccess(e.message || t('deniedPermission'));
          resetLocation();
          reject(e);
        },
        { timeout: 5000 }
      );
    });
  }, []);

  const setLocationByPostcode = useCallback(
    async (postCode: string) => {
      try {
        const userPostcode = trim(postCode);
        const data = await locationApi.get(userPostcode, {
          params: { 'api-key': geolocationKey },
        });

        if (!get(data, 'addresses', []).length) {
          throw new Error('No addresses found');
        }

        if (isObject(data)) {
          setPostCode(userPostcode);
          setLocation(data);
        }
      } catch (e) {
        resetLocation();
        throw new Error(e);
      }
    },
    [geolocationKey, resetLocation]
  );

  const changeLocation = useCallback(async (address: GeocodeAddress, googleMap: RefObject) => {
    let location = address;
    if (isGoogleMapsEnabled) {
      location = await fetchPlaceDetails(address, googleMap.current);
    }
    setLocation(location);
    const postCode = get(location, 'postalcode', null);

    if (!postCode) location.postalcode = null;

    setPostCode(postCode);
    return [postCode, location];
  }, []);

  useEffect(() => {
    Promise.all([storage.getItem('userPostcode'), storage.getItem('postCodeLocation')]).then(
      ([userPostcode, postCodeLocation]) => {
        if (get(postCodeLocation, 'isNavigatorGeolocation')) {
          getNavigatorGeolocation();
          return;
        }
        if (!isEmpty(postCodeLocation)) setLocation(postCodeLocation);
        if (userPostcode) setPostCode(userPostcode);
      }
    );
  }, []);

  useEffect(() => {
    if (!isGeocodeEnabled && postCode && !location) {
      setLocationByPostcode(postCode);
    }
  }, [postCode, location, isGeocodeEnabled]);

  const value = useMemo(
    () => ({
      location,
      setLocation,
      postCode,
      setPostCode,
      geolocationKey,
      isGeocodeEnabled,
      isGoogleMapsEnabled,
      getNavigatorGeolocation,
      changeLocation,
      resetLocation,
      setLocationByPostcode,
      googleMapsSetting,
      geoCodeSetting,
      googleApiKey: isGoogleMapsEnabled ? googleMapsSetting.key : null,
      deniedAccess,
      setDeniedAccess,
      setSaveToLocalStorage,
      saveLocalStorageLocation,
    }),
    [
      location,
      setLocation,
      postCode,
      setPostCode,
      geolocationKey,
      isGeocodeEnabled,
      getNavigatorGeolocation,
      changeLocation,
      resetLocation,
      setLocationByPostcode,
      googleMapsSetting,
      geoCodeSetting,
      deniedAccess,
      setDeniedAccess,
      isGoogleMapsEnabled,
      setSaveToLocalStorage,
    ]
  );
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

function saveLocalStorageLocation(userPostcode, postCodeLocation) {
  return Promise.all([
    storage.setItem('userPostcode', userPostcode),
    storage.setItem('postCodeLocation', postCodeLocation),
  ]);
}

function getGeolocationKey(settings) {
  const geolocationKey = get(settings, 'geolocation.key');
  return geolocationKey === '0' ? false : geolocationKey;
}
